import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import api, { brl, fmtDate } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Ticket, Trophy, History } from "lucide-react";

export default function UserDashboard() {
  const { user, loading } = useAuth();
  const [purchases, setPurchases] = useState([]);

  useEffect(() => {
    if (user) api.get("/purchases/me").then(({ data }) => setPurchases(data)).catch(() => {});
  }, [user]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-20 text-slate-400">Carregando...</div>;
  if (!user) return <Navigate to="/login" replace />;

  const paid = purchases.filter((p) => p.status === "paid");
  const pending = purchases.filter((p) => p.status === "pending");

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.3em] text-amber-200/80 mb-2">Meu painel</p>
        <h1 className="heading text-3xl sm:text-4xl font-extrabold text-slate-50">Olá, <span className="gold-gradient-text">{user.name?.split(" ")[0]}</span></h1>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        <MetricCard icon={<Ticket className="w-5 h-5" />} label="Cotas confirmadas" value={paid.reduce((a, p) => a + p.quantity, 0)} />
        <MetricCard icon={<History className="w-5 h-5" />} label="Compras pendentes" value={pending.length} />
        <MetricCard icon={<Trophy className="w-5 h-5" />} label="Total investido" value={brl(paid.reduce((a, p) => a + p.total, 0))} />
      </div>

      <Tabs defaultValue="cotas">
        <TabsList className="bg-slate-900/50 border border-white/5">
          <TabsTrigger value="cotas" data-testid="tab-cotas" className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-200">Minhas Cotas</TabsTrigger>
          <TabsTrigger value="historico" data-testid="tab-historico" className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-200">Histórico</TabsTrigger>
        </TabsList>

        <TabsContent value="cotas" className="mt-6">
          {paid.length === 0 ? (
            <div className="card-luxury rounded-2xl p-10 text-center text-slate-400">Você ainda não possui cotas.</div>
          ) : (
            <div className="space-y-4">
              {paid.map((p) => (
                <PurchaseRow key={p.purchase_id} p={p} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="historico" className="mt-6">
          {purchases.length === 0 ? (
            <div className="card-luxury rounded-2xl p-10 text-center text-slate-400">Sem histórico ainda.</div>
          ) : (
            <div className="space-y-4">
              {purchases.map((p) => <PurchaseRow key={p.purchase_id} p={p} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const MetricCard = ({ icon, label, value }) => (
  <div className="card-luxury rounded-2xl p-5">
    <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-200 mb-3">{icon}</div>
    <div className="text-xs uppercase tracking-widest text-slate-400">{label}</div>
    <div className="heading text-2xl font-bold text-slate-50 mt-1">{value}</div>
  </div>
);

const PurchaseRow = ({ p }) => (
  <div className="card-luxury rounded-2xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
    <div className="flex-1">
      <div className="flex items-center gap-2">
        <h3 className="heading text-lg font-semibold text-slate-50">{p.raffle_title}</h3>
        <Badge className={p.status === "paid" ? "bg-emerald-500/15 border border-emerald-400/30 text-emerald-200" : "bg-amber-500/10 border border-amber-500/30 text-amber-200"}>
          {p.status === "paid" ? "Pago" : "Pendente"}
        </Badge>
      </div>
      <p className="text-xs text-slate-400 mt-1">Pedido {p.order_id} · {fmtDate(p.created_at)}</p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {p.tickets.slice(0, 8).map((t) => (
          <span key={t} className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-950 border border-amber-500/20 text-amber-200">
            #{String(t).padStart(6, "0")}
          </span>
        ))}
        {p.tickets.length > 8 && <span className="text-[10px] text-slate-500">+{p.tickets.length - 8}</span>}
      </div>
    </div>
    <div className="text-right">
      <div className="text-xs uppercase tracking-widest text-slate-400">{p.quantity} cotas</div>
      <div className="heading text-xl font-bold gold-gradient-text">{brl(p.total)}</div>
    </div>
  </div>
);
